/* eslint-disable unicorn/prevent-abbreviations, unicorn/no-null, no-magic-numbers, promise/prefer-await-to-callbacks, @typescript-eslint/use-unknown-in-catch-callback-variable */

import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Loader, Menu, Text } from '@mantine/core';
import {
    ChevronDown,
    ChevronRight,
    File,
    FilePlus,
    Folder,
    FolderOpen,
    FolderPlus,
    MoreHorizontal,
    Pencil,
    Trash2
} from 'lucide-react';
import {
    FileTreeDropState,
    FileTreeMoveStatus,
    FileTreeNodeKind
} from './FileTree.enums';
import { fileTreeItemNameSchema } from './FileTree.schema';
import type { FileTreeRowProps } from './FileTree.types';
import {
    FILE_TREE_PATH_MIME,
    getFileTreeDropState,
    getFileTreeMoveStatus,
    hasFileTreeDragData
} from './FileTree.utils';
import { FileTreeInlineRename } from './FileTreeInlineRename';
import { cn } from '@/utils';

export const FileTreeRow: FC<FileTreeRowProps> = ({
    node,
    depth,
    expandedFolders,
    activeFilePath,
    isReadOnly,
    renamingPath,
    pendingRenamePath,
    pendingCreatePath,
    pendingDeletePaths,
    dragSource,
    actions
}) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const [anchorPos, setAnchorPos] = useState({ x: 0, y: 0 });
    const [renameError, setRenameError] = useState<string | undefined>();
    const renameInputRef = useRef<HTMLInputElement>(null);

    const isRenaming = renamingPath === node.path;
    const isPendingRename = pendingRenamePath === node.path;
    const isPendingCreate = pendingCreatePath === node.path;
    const isPendingDelete = pendingDeletePaths.has(node.path);
    const isPending = isPendingRename || isPendingCreate || isPendingDelete;

    const isDraggable = !isReadOnly && !isRenaming && !isPending;

    const [isDropHovered, setIsDropHovered] = useState(false);
    const dragCounterRef = useRef(0);
    const autoExpandTimerRef = useRef<ReturnType<
        typeof globalThis.setTimeout
    > | null>(null);

    const moveStatus = dragSource
        ? getFileTreeMoveStatus(dragSource, node.path)
        : FileTreeMoveStatus.Allowed;

    const clearAutoExpand = () => {
        if (autoExpandTimerRef.current !== null) {
            globalThis.clearTimeout(autoExpandTimerRef.current);
            autoExpandTimerRef.current = null;
        }
    };

    useEffect(() => clearAutoExpand, []);

    const handleRowDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        if (!isDraggable) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.setData(FILE_TREE_PATH_MIME, node.path);

        e.dataTransfer.setData('text/plain', node.path);
        e.dataTransfer.effectAllowed = 'move';
        actions.onDragSourceStart(node.path, node.kind);
    };

    const handleRowDragEnd = () => {
        actions.onDragSourceEnd();
        dragCounterRef.current = 0;
        setIsDropHovered(false);
        clearAutoExpand();
    };

    useEffect(() => {
        if (!isRenaming) {
            return;
        }
        const id = globalThis.setTimeout(() => {
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
        }, 30);
        return () => {
            globalThis.clearTimeout(id);
        };
    }, [isRenaming]);

    const paddingLeft = `${depth * 16 + 12}px`;

    const submitRename = (raw: string) => {
        const newName = raw.trim();
        if (!newName || newName === node.name) {
            actions.onCancelRename();
            return;
        }
        fileTreeItemNameSchema
            .validate(newName)
            .then(() => {
                actions.onSubmitRename(node.path, newName, node.kind);
            })
            .catch((error: Error) => {
                setRenameError(error.message);
            });
    };

    const handleRowKeyDown = (e: React.KeyboardEvent) => {
        if (isPending) {
            return;
        }
        if (isRenaming) {
            return;
        }
        if (e.key === 'F2' && !isReadOnly) {
            e.preventDefault();
            actions.onStartRename(node.path);
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            if (node.kind === FileTreeNodeKind.Folder) {
                actions.onToggleFolder(node.path);
            } else {
                actions.onFileClick(node.path);
            }
        }
    };

    const showActions = !isReadOnly && !isRenaming && !isPending;
    const guardedContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!showActions) {
            return;
        }
        setAnchorPos({ x: e.clientX, y: e.clientY });
        setMenuOpen(true);
    };
    const openMenuFromIcon = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (!showActions) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setAnchorPos({ x: rect.right, y: rect.bottom });
        setMenuOpen((prev) => !prev);
    };
    const renderAnchor = () => (
        <Box
            aria-hidden
            style={{
                position: 'fixed',
                left: anchorPos.x,
                top: anchorPos.y,
                width: 0,
                height: 0,
                pointerEvents: 'none'
            }}
        />
    );

    if (node.kind === FileTreeNodeKind.Folder) {
        const isExpanded = expandedFolders.has(node.path);

        const dropState = getFileTreeDropState(isDropHovered, moveStatus);
        let folderIcon = <Folder size={14} />;

        if (isPending) {
            folderIcon = <Loader size={12} />;
        } else if (isExpanded) {
            folderIcon = <FolderOpen size={14} />;
        }

        const folderClassName = cn(
            'group flex h-8 cursor-pointer items-center gap-1 pr-3',
            dropState === FileTreeDropState.Idle &&
                !isPending &&
                'hover:bg-gray-2',
            dropState === FileTreeDropState.Valid &&
                'bg-blue-0 outline outline-2 -outline-offset-1 outline-blue-5',
            dropState === FileTreeDropState.Invalid &&
                'bg-red-0 outline outline-2 -outline-offset-1 outline-red-5',
            isPending && 'opacity-60 cursor-default'
        );

        const handleFolderDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
            if (!hasFileTreeDragData(e.dataTransfer)) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current += 1;
            if (dragCounterRef.current === 1) {
                setIsDropHovered(true);

                if (!isExpanded && moveStatus === FileTreeMoveStatus.Allowed) {
                    autoExpandTimerRef.current = globalThis.setTimeout(() => {
                        autoExpandTimerRef.current = null;
                        actions.onToggleFolder(node.path);
                    }, 600);
                }
            }
        };

        const handleFolderDragOver = (e: React.DragEvent<HTMLDivElement>) => {
            if (!hasFileTreeDragData(e.dataTransfer)) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect =
                moveStatus === FileTreeMoveStatus.Allowed ? 'move' : 'none';
        };

        const handleFolderDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
            if (!hasFileTreeDragData(e.dataTransfer)) {
                return;
            }
            e.stopPropagation();
            dragCounterRef.current -= 1;
            if (dragCounterRef.current <= 0) {
                dragCounterRef.current = 0;
                setIsDropHovered(false);
                clearAutoExpand();
            }
        };

        const handleFolderDrop = (e: React.DragEvent<HTMLDivElement>) => {
            if (!hasFileTreeDragData(e.dataTransfer)) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            dragCounterRef.current = 0;
            setIsDropHovered(false);
            clearAutoExpand();
            if (moveStatus === FileTreeMoveStatus.Allowed) {
                actions.onMove(node.path);
            }
        };

        return (
            <>
                <Box
                    aria-disabled={isPending}
                    className={folderClassName}
                    draggable={isDraggable}
                    role="button"
                    style={{ paddingLeft }}
                    tabIndex={0}
                    onClick={() => {
                        if (isPending) {
                            return;
                        }
                        if (isRenaming) {
                            return;
                        }
                        actions.onToggleFolder(node.path);
                    }}
                    onContextMenu={guardedContextMenu}
                    onDragEnd={handleRowDragEnd}
                    onDragEnter={handleFolderDragEnter}
                    onDragLeave={handleFolderDragLeave}
                    onDragOver={handleFolderDragOver}
                    onDragStart={handleRowDragStart}
                    onDrop={handleFolderDrop}
                    onKeyDown={handleRowKeyDown}
                >
                    {isExpanded ? (
                        <ChevronDown size={12} />
                    ) : (
                        <ChevronRight size={12} />
                    )}
                    {folderIcon}
                    {isRenaming ? (
                        <FileTreeInlineRename
                            defaultValue={node.name}
                            error={renameError}
                            handleCancel={actions.onCancelRename}
                            handleSubmit={submitRename}
                            inputRef={renameInputRef}
                        />
                    ) : (
                        <Text className="flex-1 truncate" size="xs">
                            {node.name}
                        </Text>
                    )}
                    <ActionIcon
                        aria-label="Folder actions"
                        className={cn(
                            'opacity-0 transition-opacity',
                            showActions
                                ? 'group-hover:opacity-100 focus:opacity-100'
                                : 'pointer-events-none invisible'
                        )}
                        size="xs"
                        variant="subtle"
                        onClick={openMenuFromIcon}
                    >
                        <MoreHorizontal size={12} />
                    </ActionIcon>
                </Box>
                <Menu
                    withinPortal
                    opened={menuOpen}
                    position="bottom-start"
                    shadow="md"
                    onChange={setMenuOpen}
                >
                    <Menu.Target>{renderAnchor()}</Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            disabled={isReadOnly}
                            leftSection={<FilePlus size={14} />}
                            onClick={() => {
                                actions.onNewFileIn(node.path);
                            }}
                        >
                            New File…
                        </Menu.Item>
                        <Menu.Item
                            disabled={isReadOnly}
                            leftSection={<FolderPlus size={14} />}
                            onClick={() => {
                                actions.onNewFolderIn(node.path);
                            }}
                        >
                            New Folder…
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                            disabled={isReadOnly}
                            leftSection={<Pencil size={14} />}
                            rightSection={
                                <Text c="dimmed" size="xs">
                                    F2
                                </Text>
                            }
                            onClick={() => {
                                actions.onStartRename(node.path);
                            }}
                        >
                            Rename
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                            color="red"
                            disabled={isReadOnly}
                            leftSection={<Trash2 size={14} />}
                            onClick={() => {
                                actions.onDeleteFolder(node.path);
                            }}
                        >
                            Delete
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
                {isExpanded
                    ? node.children.map((child) => (
                          <FileTreeRow
                              actions={actions}
                              activeFilePath={activeFilePath}
                              depth={depth + 1}
                              dragSource={dragSource}
                              expandedFolders={expandedFolders}
                              isReadOnly={isReadOnly}
                              key={`${child.kind}-${child.name}`}
                              node={child}
                              pendingCreatePath={pendingCreatePath}
                              pendingDeletePaths={pendingDeletePaths}
                              pendingRenamePath={pendingRenamePath}
                              renamingPath={renamingPath}
                          />
                      ))
                    : null}
            </>
        );
    }

    const isActive = node.path === activeFilePath;
    return (
        <>
            <Box
                aria-disabled={isPending}
                className={cn(
                    'group flex h-8 cursor-pointer items-center gap-1 pr-3',
                    !isPending && 'hover:bg-gray-2',
                    isActive && 'bg-blue-0',
                    isPending && 'opacity-60 cursor-default'
                )}
                draggable={isDraggable}
                role="button"
                style={{ paddingLeft }}
                tabIndex={0}
                onClick={() => {
                    if (isPending) {
                        return;
                    }
                    if (isRenaming) {
                        return;
                    }
                    actions.onFileClick(node.path);
                }}
                onContextMenu={guardedContextMenu}
                onDragEnd={handleRowDragEnd}
                onDragStart={handleRowDragStart}
                onKeyDown={handleRowKeyDown}
            >
                {isPending ? <Loader size={12} /> : <File size={14} />}
                {isRenaming ? (
                    <FileTreeInlineRename
                        defaultValue={node.name}
                        error={renameError}
                        handleCancel={actions.onCancelRename}
                        handleSubmit={submitRename}
                        inputRef={renameInputRef}
                    />
                ) : (
                    <Text className="flex-1 truncate" size="xs">
                        {node.name}
                    </Text>
                )}
                <ActionIcon
                    aria-label="File actions"
                    className={cn(
                        'opacity-0 transition-opacity',
                        showActions
                            ? 'group-hover:opacity-100 focus:opacity-100'
                            : 'pointer-events-none invisible'
                    )}
                    size="xs"
                    variant="subtle"
                    onClick={openMenuFromIcon}
                >
                    <MoreHorizontal size={12} />
                </ActionIcon>
            </Box>
            <Menu
                withinPortal
                opened={menuOpen}
                position="bottom-start"
                shadow="md"
                onChange={setMenuOpen}
            >
                <Menu.Target>{renderAnchor()}</Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        disabled={isReadOnly}
                        leftSection={<Pencil size={14} />}
                        rightSection={
                            <Text c="dimmed" size="xs">
                                F2
                            </Text>
                        }
                        onClick={() => {
                            actions.onStartRename(node.path);
                        }}
                    >
                        Rename
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                        color="red"
                        disabled={isReadOnly}
                        leftSection={<Trash2 size={14} />}
                        onClick={() => {
                            actions.onDeleteFile(node.path);
                        }}
                    >
                        Delete
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </>
    );
};
